import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Typography,
  Alert,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import UserService from '../services/UserService';
import SellerService from '../services/SellerService';
import { SellerPermissions } from '../models/SellerType';
import { useAppSelector, useAppDispatch } from '../store/Hooks';
import { updateUser } from '../store/slices/UserSlice';
import { startImpersonation } from '../store/slices/ShadowUserSlice';
import Spinner from '../components/Spinner';
import AlertUtils from '../utils/AlertUtil';
import translator from '../theme/translator.json';
import Utils from '../utils/Utils';

const Sellers: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user, language } = useAppSelector((state) => state.user);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openConfirmAddDialog, setOpenConfirmAddDialog] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState<any>(null);
  const [vendeurs, setVendeurs] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const navigate = useNavigate();

  // Fonctions de validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string): boolean => password.length >= 8 && /[A-Z]/.test(password);

  const validateName = (name: string): boolean => name.trim().length >= 3;

  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!validateName(newVendeur.name)) {
      errors.push(Utils.getTranslation(language, translator.pages.sellers.validation.nameMinLength));
    }

    if (!validateEmail(newVendeur.email)) {
      errors.push(Utils.getTranslation(language, translator.pages.sellers.validation.invalidEmail));
    }

    if (!validatePassword(newVendeur.password)) {
      errors.push(Utils.getTranslation(language, translator.pages.sellers.validation.passwordRequirements));
    }

    if (newVendeur.password !== newVendeur.confirmPassword) {
      errors.push(Utils.getTranslation(language, translator.pages.sellers.validation.passwordsDoNotMatch));
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  // États pour le formulaire
  const [newVendeur, setNewVendeur] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  // Charger les vendeurs
  useEffect(() => {
    const loadSellers = async () => {
      if (!user) return;

      try {
        setLoading(true);
        const sellers = await UserService.getAllSellers(user.id);
        const activeSellers = sellers.filter((seller) => seller.visibility !== 'HIDDEN');
        setVendeurs(activeSellers);
      } catch (err: any) {
        setError(err.message || Utils.getTranslation(language, translator.pages.sellers.errors.loadingError));
      } finally {
        setLoading(false);
      }
    };

    loadSellers();
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewVendeur((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleOpenAddConfirmDialog = () => {
    // Validation du formulaire
    if (!validateForm()) {
      return;
    }
    setError(null);
    setOpenAddDialog(false);
    setOpenConfirmAddDialog(true);
  };

  const handleAddVendeur = async () => {
    setOpenConfirmAddDialog(false);
    try {
      if (!user) {
        setError(Utils.getTranslation(language, translator.pages.sellers.errors.mustBeLoggedIn));
        return;
      }

      setLoading(true);
      setError(null);

      // Permissions par défaut pour le vendeur
      const defaultPermissions: SellerPermissions = {
        canCreateClients: true,
        canEditClients: true,
        canDeleteClients: false,
        canViewAllClients: false,
        canManageSuivis: true,
      };

      // Appel au service pour créer le vendeur (la licence est ajoutée automatiquement)
      const result = await SellerService.createSeller(
        newVendeur.email,
        newVendeur.password,
        newVendeur.name,
        '',
        user.id,
        defaultPermissions,
        promoCode,
      );

      // Recharger la liste des vendeurs
      const updatedSellers = await UserService.getAllSellers(user.id);
      const activeSellers = updatedSellers.filter((seller) => seller.visibility !== 'HIDDEN');
      setVendeurs(activeSellers);

      setNewVendeur({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
      });
      setPromoCode('');

      // Message de succès
      const message = (result as any)?.message || Utils.getTranslation(language, translator.pages.sellers.success.sellerCreated);
      AlertUtils.createSuccessAlert(message, dispatch);
    } catch (err: any) {
      const errorMessage = err.message || Utils.getTranslation(language, translator.pages.sellers.errors.createError);
      AlertUtils.createErrorAlert(errorMessage, dispatch);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAccount = (seller: any) => {
    const mainUserSettings = {
      pdfExportContactInformation: user.pdfExportContactInformation ?? true,
      pdfExportAdditionalInformation: user.pdfExportAdditionalInformation ?? true,
      pdfExportClientDescription: user.pdfExportClientDescription ?? true,
      pdfExportFollowUpHistory: user.pdfExportFollowUpHistory ?? true,
      language: user.language ?? 'fr',
      hidePriority: user.hidePriority ?? false,
      showAdressField: user.showAdressField ?? false,
      showAllInBanqueContacts: user.showAllInBanqueContacts ?? false,
      showVendeurSuivis: user.showVendeurSuivis ?? false,
      showVisites: user.showVisites ?? false,
    };

    // Sauvegarder les données du parent dans le seller pour pouvoir revenir
    const sellerWithParent = {
      ...seller,
      ...mainUserSettings, // We do not consider the seller's settings
      originalUser: user, // On garde une référence au parent
    };
    dispatch(startImpersonation(sellerWithParent));
    dispatch(updateUser(sellerWithParent));
    // redirect to dashboard
    navigate('/');
  };

  const handleDeleteClick = (seller: any) => {
    setSelectedSeller(seller);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (!user || !selectedSeller) return;

      setLoading(true);
      const result = await SellerService.deleteSeller(user.id, selectedSeller.id);

      if (result.success) {
        // Recharger la liste des vendeurs
        const updatedSellers = await UserService.getAllSellers(user.id);
        const activeSellers = updatedSellers.filter((seller) => seller.visibility !== 'HIDDEN');
        setVendeurs(activeSellers);
        setOpenDeleteDialog(false);
        setSelectedSeller(null);
        const message = (result as any)?.message || Utils.getTranslation(language, translator.pages.sellers.success.sellerDeleted);
        AlertUtils.createSuccessAlert(message, dispatch);
      }
    } catch (err: any) {
      const errorMessage = err.message || Utils.getTranslation(language, translator.pages.sellers.errors.deleteError);
      AlertUtils.createErrorAlert(errorMessage, dispatch);
    } finally {
      setLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: Utils.getTranslation(language, translator.pages.sellers.table.name),
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'email',
      headerName: Utils.getTranslation(language, translator.pages.sellers.table.email),
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: 'createdAt',
      headerName: Utils.getTranslation(language, translator.pages.sellers.table.creationDate),
      width: 150,
    },
    {
      field: 'actions',
      headerName: Utils.getTranslation(language, translator.pages.sellers.table.actions),
      minWidth: 220,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleViewAccount(params.row)}
          >
            {Utils.getTranslation(language, translator.pages.sellers.table.viewButton)}
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => handleDeleteClick(params.row)}
          >
            {Utils.getTranslation(language, translator.pages.sellers.table.deleteButton)}
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Layout title={Utils.getTranslation(language, translator.pages.sellers.title)}>
      <Box sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          mt: 3,
        }}>
          <Typography variant="h5" component="h1" sx={{ color: 'white' }}>
            {Utils.getTranslation(language, translator.pages.sellers.header)}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDialog(true)}
          >
            {Utils.getTranslation(language, translator.pages.sellers.addSellerButton)}
          </Button>
        </Box>

        <DataGrid
          rows={vendeurs}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          autoHeight
          components={{
            Toolbar: GridToolbar,
          }}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          sx={{
            backgroundColor: 'white',
            '.MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold',
            },
          }}
        />

        <Dialog open={openAddDialog} onClose={() => {
          setOpenAddDialog(false);
          setPromoCode('');
          setValidationErrors([]);
        }} maxWidth="sm" fullWidth>
          <DialogTitle>{Utils.getTranslation(language, translator.pages.sellers.addDialog.title)}</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                {error}
              </Alert>
            )}
            <Box sx={{
              display: 'flex', flexDirection: 'column', gap: 2, mt: 2,
            }}>
              <TextField
                label={Utils.getTranslation(language, translator.pages.sellers.form.name)}
                name="name"
                value={newVendeur.name}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
                required
              />
              <TextField
                label={Utils.getTranslation(language, translator.pages.sellers.form.email)}
                name="email"
                type="email"
                value={newVendeur.email}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
                required
              />

              <TextField
                label={Utils.getTranslation(language, translator.pages.sellers.form.password)}
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={newVendeur.password}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon /> }
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label={Utils.getTranslation(language, translator.pages.sellers.form.confirmPassword)}
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={newVendeur.confirmPassword}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {validationErrors.length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {validationErrors.map((validationError, index) => (
                      <li key={index}>{validationError}</li>
                    ))}
                  </ul>
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenAddDialog(false);
              setValidationErrors([]);
            }}>{Utils.getTranslation(language, translator.general.cancel)}</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenAddConfirmDialog}
              disabled={loading || !newVendeur.name || !newVendeur.email || !newVendeur.password || !newVendeur.confirmPassword}
            >
              {loading
                ? Utils.getTranslation(language, translator.pages.sellers.addDialog.addingButton)
                : Utils.getTranslation(language, translator.pages.sellers.addDialog.addButton)}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de confirmation d'ajout */}
        <Dialog open={openConfirmAddDialog} onClose={() => setOpenConfirmAddDialog(false)}>
          <DialogTitle>{Utils.getTranslation(language, translator.pages.sellers.confirmAddDialog.title)}</DialogTitle>
          <DialogContent>
            <Typography>
              {Utils.getTranslation(language, translator.pages.sellers.confirmAddDialog.warning)}
            </Typography>
            <Typography sx={{ mt: 1 }}>
              {Utils.getTranslation(language, translator.pages.sellers.confirmAddDialog.billingInfo)}
            </Typography>
            <Typography sx={{ mt: 2 }}>
              {Utils.getTranslation(language, translator.pages.sellers.confirmAddDialog.continuePrompt)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirmAddDialog(false)}>{Utils.getTranslation(language, translator.general.cancel)}</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddVendeur}
              disabled={loading}
            >
              {loading
                ? Utils.getTranslation(language, translator.pages.sellers.confirmAddDialog.addingButton)
                : Utils.getTranslation(language, translator.pages.sellers.confirmAddDialog.confirmButton)}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de confirmation de suppression */}
        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
          <DialogTitle>{Utils.getTranslation(language, translator.pages.sellers.deleteDialog.title)}</DialogTitle>
          <DialogContent>
            <Typography>
              {Utils.getTranslation(language, translator.pages.sellers.deleteDialog.description, { sellerName: selectedSeller?.name })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>{Utils.getTranslation(language, translator.general.cancel)}</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteConfirm}
              disabled={loading}
            >
              {loading
                ? Utils.getTranslation(language, translator.pages.sellers.deleteDialog.deletingButton)
                : Utils.getTranslation(language, translator.pages.sellers.deleteDialog.deleteButton)}
            </Button>
          </DialogActions>
        </Dialog>

        <Spinner show={loading} />
      </Box>
    </Layout>
  );
};

export default Sellers;
